import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';

interface SeriesCard {
  id: string;
  label: string;
  title: string;
  slug: string;
  imageUrl: string;
  status: string;
}

const seriesCards: SeriesCard[] = [
  {
    id: 'spotify',
    label: 'UI再現実験',
    title: 'Spotify完全再現',
    slug: '/series/spotify-ui',
    imageUrl:
      'https://public.readdy.ai/ai/img_res/1fa05106ca6c4ecd4126269f710f6729.jpg',
    status: '進行中',
  },
  {
    id: 'pikachu',
    label: 'キャラクターサイト制作',
    title: 'ピカチュウ風サイトを作ってみた',
    slug: '/series/build-log',
    imageUrl:
      'https://readdy.ai/api/search-image?query=Pikachu-themed%20website%20design%20on%20a%20laptop%20screen%2C%20bright%20yellow%20and%20warm%20color%20palette%2C%20cute%20character-inspired%20UI%20elements%2C%20playful%20typography%2C%20Pokemon-style%20card%20layout%2C%20editorial%20flat%20lay%20photography%2C%20warm%20cream%20background%2C%20soft%20studio%20lighting%2C%20high%20detail%20product%20shot&width=800&height=600&seq=hero-pikachu-01&orientation=landscape',
    status: '進行中',
  },
  {
    id: 'mario',
    label: 'ゲームUI再現',
    title: 'スーパーマリオブラザーズを作ってみた',
    slug: '/series/build-log',
    imageUrl:
      'https://readdy.ai/api/search-image?query=Super%20Mario%20Bros%20inspired%20web%20game%20interface%20on%20a%20monitor%20screen%2C%20retro%20pixel%20art%20style%20UI%2C%20red%20and%20blue%20color%20scheme%2C%20classic%208-bit%20game%20elements%2C%20score%20display%20and%20level%20indicator%2C%20editorial%20photography%20style%2C%20warm%20off-white%20background%2C%20soft%20natural%20lighting%2C%20nostalgic%20game%20design&width=800&height=600&seq=hero-mario-01&orientation=landscape',
    status: '進行中',
  },
  {
    id: 'notion',
    label: 'ツールUI再現',
    title: 'Notion風エディタを再現する',
    slug: '/series/ai-article-flow',
    imageUrl:
      'https://readdy.ai/api/search-image?query=Notion-inspired%20document%20editor%20interface%20on%20a%20desktop%20screen%2C%20clean%20minimal%20white%20and%20light%20gray%20UI%2C%20block-based%20text%20editor%2C%20sidebar%20navigation%2C%20clean%20typography%2C%20editorial%20photography%20style%2C%20warm%20cream%20paper%20background%2C%20soft%20diffused%20lighting%2C%20professional%20workspace%20aesthetic&width=800&height=600&seq=hero-notion-01&orientation=landscape',
    status: '進行中',
  },
];

export function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  const goTo = useCallback(
    (index: number) => {
      if (isTransitioning) return;
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrent(index);
        setIsTransitioning(false);
      }, 200);
    },
    [isTransitioning]
  );

  const goNext = useCallback(() => {
    goTo((current + 1) % seriesCards.length);
  }, [current, goTo]);

  const goPrev = useCallback(() => {
    goTo((current - 1 + seriesCards.length) % seriesCards.length);
  }, [current, goTo]);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % seriesCards.length);
    }, 5000);
  }, []);

  useEffect(() => {
    resetTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [resetTimer]);

  const handleDotClick = (index: number) => {
    goTo(index);
    resetTimer();
  };

  const handlePrev = () => {
    goPrev();
    resetTimer();
  };

  const handleNext = () => {
    goNext();
    resetTimer();
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = null;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 40) {
      if (diff > 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  const card = seriesCards[current];

  return (
    <section className="pt-20 sm:pt-24 pb-10 sm:pb-14 bg-[#FDFAF6]">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">

        {/* Eyebrow label */}
        <div className="mb-5 sm:mb-6 flex items-center gap-2">
          <span
            className="inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-widest uppercase text-[#5B7B6F] border border-[#5B7B6F]/30 px-2.5 py-1 rounded-full"
            style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#5B7B6F] animate-pulse inline-block"></span>
            進行中の制作シリーズ
          </span>
        </div>

        {/* Main copy */}
        <h1
          className="font-bold leading-[1.25] tracking-tight text-[#2C2416] mb-3 sm:mb-4"
          style={{ fontFamily: "'Noto Serif JP', 'Georgia', serif" }}
        >
          {/* スマホ：2行レイアウト / PC：1行 */}
          <span className="block sm:hidden">
            <span className="text-[1.75rem] leading-[1.2]">
              AIで<em className="not-italic relative inline-block">
                <span className="relative z-10">"あの"</span>
                <span
                  className="absolute bottom-0.5 left-0 w-full h-[6px] bg-[#5B7B6F]/15 rounded-sm"
                  aria-hidden="true"
                ></span>
              </em>
            </span>
            <span className="block text-[1.75rem] leading-[1.2] mt-0.5">
              有名サービスを<span className="text-[#5B7B6F]">完全再現！</span>
            </span>
          </span>
          {/* PC：1行 */}
          <span className="hidden sm:inline text-[2.8rem]">
            AIで<em className="not-italic relative inline-block">
              <span className="relative z-10">"あの"</span>
              <span
                className="absolute bottom-1 left-0 w-full h-[7px] bg-[#5B7B6F]/15 rounded-sm"
                aria-hidden="true"
              ></span>
            </em>
            有名サービスを<span className="text-[#5B7B6F]">完全再現！</span>
          </span>
        </h1>

        {/* Sub copy */}
        <p
          className="text-sm sm:text-base text-[#7A6E62] leading-relaxed mb-8 sm:mb-10"
          style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
        >
          考えて、失敗して、直したところまで残す実践ログ。
        </p>

        {/* Carousel */}
        <div
          className="relative rounded-xl overflow-hidden cursor-pointer select-none"
          style={{ aspectRatio: '4/3' }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Image */}
          <div
            className="absolute inset-0 transition-opacity duration-300"
            style={{ opacity: isTransitioning ? 0 : 1 }}
          >
            <img
              src={card.imageUrl}
              alt={card.title}
              className="w-full h-full object-cover object-top"
            />
            {/* Subtle overlay for readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
          </div>

          {/* Status badge top-left */}
          <div className="absolute top-3 left-3 z-10">
            <span
              className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-wider uppercase bg-[#FDFAF6]/90 text-[#5B7B6F] px-2.5 py-1 rounded-full"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#5B7B6F] animate-pulse inline-block"></span>
              {card.label}
            </span>
          </div>

          {/* Prev / Next arrows */}
          <button
            onClick={handlePrev}
            className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-[#FDFAF6]/80 text-[#2C2416] hover:bg-[#FDFAF6] transition-colors cursor-pointer"
            aria-label="前へ"
          >
            <i className="ri-arrow-left-s-line text-base"></i>
          </button>
          <button
            onClick={handleNext}
            className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-[#FDFAF6]/80 text-[#2C2416] hover:bg-[#FDFAF6] transition-colors cursor-pointer"
            aria-label="次へ"
          >
            <i className="ri-arrow-right-s-line text-base"></i>
          </button>

          {/* Dots */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5">
            {seriesCards.map((_, i) => (
              <button
                key={i}
                onClick={() => handleDotClick(i)}
                className={`rounded-full transition-all duration-300 cursor-pointer ${
                  i === current
                    ? 'w-5 h-1.5 bg-[#FDFAF6]'
                    : 'w-1.5 h-1.5 bg-[#FDFAF6]/50 hover:bg-[#FDFAF6]/80'
                }`}
                aria-label={`${i + 1}枚目`}
              />
            ))}
          </div>
        </div>

        {/* Card title below carousel */}
        <div
          className="mt-4 flex items-center justify-between"
          style={{ opacity: isTransitioning ? 0 : 1, transition: 'opacity 0.2s' }}
        >
          <div>
            <p
              className="text-xs text-[#9A8E84] mb-0.5"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              {card.label}
            </p>
            <p
              className="text-sm font-semibold text-[#2C2416]"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              {card.title}
            </p>
          </div>
          <Link
            to={card.slug}
            className="inline-flex items-center gap-1 text-xs text-[#5B7B6F] font-medium hover:underline cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
          >
            シリーズを見る
            <i className="ri-arrow-right-line text-xs"></i>
          </Link>
        </div>

        {/* Divider */}
        <div className="mt-8 border-t border-[#E8E0D5]"></div>

        {/* Bottom CTA */}
        <div className="pt-6 flex flex-wrap items-center justify-between gap-3">
          <p
            className="text-xs text-[#9A8E84]"
            style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
          >
            全{seriesCards.length}シリーズ進行中
          </p>
          <Link
            to="/series"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4A3F35] hover:text-[#2C2416] transition-colors cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
          >
            すべてのシリーズを見る
            <i className="ri-arrow-right-line text-sm"></i>
          </Link>
        </div>

      </div>
    </section>
  );
}
